import { Clock, ExternalLink, FolderOpen, X } from 'lucide-react';
import React from 'react';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { GlobalCalendarEvent, EVENT_TYPE_COLORS, EVENT_TYPE_LABELS } from '../../types/calendar';

interface CalendarEventPopoverProps {
  event: GlobalCalendarEvent | null;
  onClose: () => void;
  anchorRect?: DOMRect | null;
}

const CalendarEventPopover: React.FC<CalendarEventPopoverProps> = ({
  event,
  onClose,
  anchorRect,
}) => {
  const navigate = useNavigate();

  if (!event) return null;

  const colors = EVENT_TYPE_COLORS[event.type];
  const startDate = parseISO(event.startDate);
  const endDate = parseISO(event.endDate);

  const handleNavigate = () => {
    navigate(event.url);
    onClose();
  };

  // Position the popover near the anchor element
  const style: React.CSSProperties = anchorRect
    ? {
        position: 'fixed',
        top: Math.min(anchorRect.bottom + 4, window.innerHeight - 240),
        left: Math.min(anchorRect.left, window.innerWidth - 320),
        zIndex: 50,
      }
    : {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 50,
      };

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div style={style} className="w-80 bg-white rounded-lg shadow-xl border border-gray-200 p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-2">
            <div className={`w-3 h-3 rounded-full ${colors.dot}`} />
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors.bg} ${colors.text}`}
            >
              {EVENT_TYPE_LABELS[event.type]}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <h3 className="text-sm font-semibold text-gray-900 mb-2">{event.title}</h3>

        <div className="space-y-2 text-xs text-gray-600">
          <div className="flex items-center space-x-2">
            <Clock className="h-3.5 w-3.5 text-gray-400" />
            {event.allDay ? (
              <span>{format(startDate, 'MMM d, yyyy')}</span>
            ) : (
              <span>
                {format(startDate, 'MMM d, yyyy HH:mm')} - {format(endDate, 'HH:mm')}
              </span>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <FolderOpen className="h-3.5 w-3.5 text-gray-400" />
            <span>{event.projectName}</span>
          </div>

          {event.status && (
            <div className="flex items-center space-x-2">
              <span className="text-gray-400">Status:</span>
              <span className="capitalize">{event.status.replace(/_/g, ' ')}</span>
            </div>
          )}
        </div>

        <div className="mt-3 pt-3 border-t border-gray-100">
          <button
            onClick={handleNavigate}
            className="flex items-center space-x-1 text-xs font-medium text-brand-primary hover:text-brand-700 transition-colors"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            <span>Open in module</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default CalendarEventPopover;
